/**
 * Реестр алертов — единственный источник правды. §Окружение 9.
 *
 * ALERT_TYPES из 02-contracts.md §9 удалён: это был второй список имён, из 14 элементов,
 * пересекающийся с реестром лишь частично и несущий собственный словарь severity из трёх
 * значений против четырёх здесь.
 *
 * AlertType ВЫВОДИТСЯ из реестра и нигде не перечисляется вручную; severity берётся из
 * ALERT_REGISTRY[type].severity. Тест alert-registry.test.ts проверяет три вещи: второго
 * списка имён в репозитории нет, каждое поле dedupFields — существующий ключ AlertScope,
 * каждый raise(...) использует тип из реестра.
 */

export const ALERT_SEVERITIES = ['info', 'warning', 'error', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

/**
 * incident — разовое событие (случилось и прошло);
 * condition — состояние, которое держится и само себя переподтверждает.
 */
export const ALERT_KINDS = ['incident', 'condition'] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export const ALERT_ROLES = ['operator', 'approver'] as const;
export type AlertRole = (typeof ALERT_ROLES)[number];

/** Кто поднимает. systemd → CLI означает: юнит упал, алерт шлёт OnFailure-хук. */
export const ALERT_SOURCES = [
  'backend',
  'publisher',
  'signer',
  'monitor',
  'writer',
  'systemd',
] as const;
export type AlertSource = (typeof ALERT_SOURCES)[number];

/**
 * Поля, по которым считается ключ дедупликации. Каждое обязано существовать в AlertScope —
 * это утверждение теста, а не комментарий.
 */
export interface AlertScope {
  brand?: string;
  draft_id?: string;
  version?: number;
  /** Единица времени в базе и в дедупликации одна: миллисекунды. Не slot_at строкой. */
  slot_at_ms?: number;
  /** 'YYYY-MM-DD' (сутки UTC) либо 'YYYY-MM' (месяц) — какой именно, задаёт periodGrain. */
  period?: string;
  source_id?: string;
  /** Тема, породившая работу. Появляется в фазе 2 вместе с draft_generation_failed. */
  topic_id?: string;
  token_id?: string;
  watchdog_key?: string;
  unit?: string;
  invocation_id?: string;
  reason?: string;
}

export type AlertScopeField = keyof AlertScope;

export interface AlertDef {
  readonly severity: AlertSeverity;
  readonly kind: AlertKind;
  readonly dedupFields: readonly AlertScopeField[];
  readonly to: readonly AlertRole[];
  /**
   * Кто поднимает алерт. Список, а не одно значение: у `publish_failed` источников
   * действительно два, и оба законны — публикатор видит отказ Telegram, бэкенд закрывает
   * работу на путях, которых публикатор не видит вовсе (истёкший лизинг, `BAD_CLAIM_SHAPE`,
   * отказ до отправки). Одно значение здесь означало бы, что один из двух подъёмов — баг;
   * на деле баг был другой: они считали РАЗНЫЕ dedup_key, и один несостоявшийся пост давал
   * два critical-алерта (исправлено 25.08, см. 09 §14.7).
   */
  readonly raisedBy: readonly AlertSource[];
  readonly phase: 1 | 2;
  readonly periodGrain?: 'day' | 'month';
}

export const ALERT_REGISTRY = {
  // ---- фаза 1 ----
  publish_failed: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version', 'slot_at_ms'],
    to: ['operator'],
    raisedBy: ['publisher', 'backend'],
    phase: 1,
  },
  publish_deviation: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator', 'approver'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  publish_uncertain: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator', 'approver'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  publish_lease_expired: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  key_verification_failed: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version', 'reason'],
    to: ['operator'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  presend_validation_failed: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator', 'approver'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  double_publish_attempt: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  daily_cap_hard_hit: {
    severity: 'critical',
    kind: 'incident',
    dedupFields: ['brand', 'period'],
    to: ['operator'],
    raisedBy: ['publisher'],
    phase: 1,
    periodGrain: 'day',
  },
  daily_cap_blocked: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['brand', 'period'],
    to: ['operator', 'approver'],
    raisedBy: ['backend'],
    phase: 1,
    periodGrain: 'day',
  },
  canon_version_mismatch: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: ['reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  contract_version_mismatch: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: ['reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  signer_unavailable: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: [],
    to: ['operator', 'approver'],
    raisedBy: ['backend'],
    phase: 1,
  },
  signer_rate_limited: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['brand'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  signer_ledger_mismatch: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: ['period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
    periodGrain: 'day',
  },
  slot_missed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['draft_id', 'version', 'slot_at_ms'],
    to: ['operator', 'approver'],
    raisedBy: ['backend'],
    phase: 1,
  },
  /**
   * Отзыв, который публикатор не забрал за окно подписанного ключа (09 §4.3, G-001).
   * Решение человека не состоялось: пост остаётся в канале, а строка отзыва закрывается
   * подметальщиком. Инцидент, а не состояние: событие случилось и прошло, выход из него —
   * повторное нажатие кнопки человеком, и второго подъёма по тому же ключу дедупликации
   * не будет. Согласующему — обязательно: именно он думает, что пост убран.
   */
  retraction_expired: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator', 'approver'],
    raisedBy: ['backend'],
    phase: 1,
  },
  /**
   * Одна строка очереди публикаций не поддаётся выдаче: бренд работы есть в базе, но не в
   * `config/brands`, версия не читается, ключ пропал (F-069). Работа НЕ закрывается — она
   * остаётся `scheduled` и уедет следующим тиком, как только причина устранена; тик при
   * этом продолжает перебор и выдаёт работы остальных брендов. Инцидент дежурному: сама
   * себя такая строка не чинит, а до крайнего срока слота ещё есть время.
   */
  claim_row_failed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  publisher_stalled: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['watchdog_key'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  publisher_clock_skew: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['unit'],
    to: ['operator'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  /*
   * Сбор метрик канала (0012). Оба алерта — `warning`, и это не занижение: публикация от
   * них не зависит никак, метрика при молчащем сборщике показывает прочерк с причиной, а
   * не враньё. Поднимать critical за неработающий счётчик подписчиков значило бы приучить
   * оператора гасить critical не глядя.
   */
  metrics_collector_stalled: {
    severity: 'warning',
    kind: 'condition',
    // Дедуп по бренду: у каждого канала свой сборщик, и молчание одного не должно прятать
    // молчание другого.
    dedupFields: ['brand'],
    to: ['operator'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  /*
   * Отдельный тип, а не `metrics_collector_stalled` с текстом: 409 от `getUpdates` — это
   * СОСТОЯНИЕ КОНФИГУРАЦИИ (на боте выставлен вебхук либо апдейты забирает второй
   * процесс), а не сбой связи. Чинится оно другим действием — снять вебхук, найти второго
   * потребителя, — и слипаться с сетевыми отказами в одну строку не должно.
   */
  metrics_updates_conflict: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['unit'],
    to: ['operator'],
    raisedBy: ['publisher'],
    phase: 1,
  },
  run_overdue: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['watchdog_key'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  watchdog_config_mismatch: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['watchdog_key'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  unit_failed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['unit', 'invocation_id'],
    to: ['operator'],
    raisedBy: ['systemd'],
    phase: 1,
  },
  intake_auth_failures: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['token_id'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  intake_quota_exceeded: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['token_id', 'period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
    periodGrain: 'day',
  },
  conflicting_idempotent_write: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['draft_id', 'reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  schema_fingerprint_mismatch: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: ['reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  audit_chain_broken: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: [],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
  },
  backup_failed: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
    periodGrain: 'day',
  },
  alert_canary: {
    severity: 'info',
    kind: 'incident',
    dedupFields: ['period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 1,
    periodGrain: 'day',
  },

  // ---- фаза 2: типы объявлены сейчас, поднимаются позже ----
  budget_threshold: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['brand', 'period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
    periodGrain: 'month',
  },
  /**
   * Потолка бюджета за месяц нет и перенести его не с чего (F-052). До этого типа первый
   * месяц жизни системы (и любой месяц после ручного удаления строки) молча выключал NFR-1:
   * `checkBudget` не находил `budget_period`, не проверял НИ ОДНОГО порога и возвращался
   * молча — расход шёл, алерта на 80% не было. Дедуп по {бренд, месяц}: один разговор про
   * один месяц, а не письмо на каждый вызов модели.
   */
  budget_period_missing: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['brand', 'period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
    periodGrain: 'month',
  },
  cache_miss_regression: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['brand', 'period'],
    to: ['operator'],
    // Мониторинг видит деградацию кэша по своим ответам API раньше, чем бэкенд по метерингу.
    raisedBy: ['backend', 'monitor'],
    phase: 2,
    periodGrain: 'day',
  },
  /**
   * Поднимает БЭКЕНД на отчёте о фетчах, а не мониторинг, хотя фетч видит мониторинг.
   * Причина — 05-seams §8.4: правило подъёма и правило СНЯТИЯ обязаны жить в одном месте,
   * а снять condition-алерт может только тот, у кого есть таблица `alert`, то есть бэкенд
   * (`resolveAlert`). Мониторинг знает лишь своё окно попыток текущего процесса: после
   * рестарта юнита оно пусто, и источник, молчащий третью неделю, снова выглядит здоровым.
   * Авторитетное здоровье живёт в колонке `source.health`, её двигает
   * POST /intake/v1/runs/:id/fetches — там же и подъём, в той же транзакции.
   */
  source_dead: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['source_id'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  /** Вторая половина того же перехода — тот же единственный поднимающий (см. source_dead). */
  source_degraded: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['source_id'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  /**
   * Разрыв между курсором источника и началом прогона больше `catch_up_max_hours`
   * (F-044): окно догона обрезано этим потолком, и материалы старше него в прогон не
   * попадут. Инцидент, а не состояние: он относится к ОДНОМУ прогону одного источника, и
   * следующий прогон с обрезанным окном — новое событие. Дедуп по {источник, сутки},
   * чтобы длинный простой не превратился в письмо на каждый прогон.
   *
   * Поднимает мониторинг: разрыв виден тому, кто считает окно фетча.
   */
  catch_up_truncated: {
    severity: 'warning',
    kind: 'incident',
    dedupFields: ['source_id', 'period'],
    to: ['operator'],
    raisedBy: ['monitor'],
    phase: 2,
    periodGrain: 'day',
  },
  source_feed_overflow: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['source_id'],
    to: ['operator'],
    raisedBy: ['monitor'],
    phase: 2,
  },
  /**
   * Источник превысил max_materials_per_run — обрезка по ранжированию, не отказ прогона
   * (06-security-ops.md ПРАВИЛО C3-6). Потолок на источник, иначе один болтливый источник
   * вытесняет остальные.
   */
  source_flood: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['source_id'],
    to: ['operator'],
    raisedBy: ['monitor'],
    phase: 2,
  },
  run_overlap: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['watchdog_key'],
    to: ['operator'],
    // Поднимает тот, кто НЕ СМОГ взять блокировку ux_run_active, — мониторинг,
    // а не бэкенд (05-seams.md §8.4).
    raisedBy: ['monitor'],
    phase: 2,
  },
  /**
   * Прогон закрылся 'ok', но materials_seen = 0 при sources_ok > 0, либо topics_created = 0
   * два прогона подряд (06-security-ops.md ПРАВИЛО C3-20): для watchdog такой прогон
   * неотличим от продуктивного. Ловит бэкенд на закрытии прогона — история прогонов есть
   * только у него.
   */
  run_empty: {
    severity: 'warning',
    kind: 'incident',
    dedupFields: ['brand', 'period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
    periodGrain: 'day',
  },
  /**
   * Заявка генерации ПО РАСПИСАНИЮ (релиз 2026-10-14, `jobs/auto-generation.ts`) кончилась
   * без новой темы: прогон отказал, тем не нашлось, вернулась только старая тема, или заявку
   * закрыла уборка. Человека, ждущего эту заявку, нет (нет статус-бара кнопки), а повтора в
   * тот же день расписание не делает — без алерта пропавшая новость пн/ср/пт молчит.
   * `run_empty` не подходит: ручной прогон его сознательно не считает. Дедуп — бренд, вид
   * (`reason` = `format_hint`) и местные сутки: в пятницу новость и дайджест — два алерта.
   */
  auto_generation_empty: {
    severity: 'warning',
    kind: 'incident',
    dedupFields: ['brand', 'reason', 'period'],
    to: ['operator', 'approver'],
    raisedBy: ['backend'],
    phase: 2,
    periodGrain: 'day',
  },
  /**
   * Один подметальщик частого прохода обслуживания (`jobs/maintenance.ts`) отказывает
   * N тиков подряд (P2, ревизия 2026-10-18). Подметальщики изолированы друг от друга, как
   * части ответа на claims (F-069): отказ одного больше не отменяет остальные, — но молча
   * отказывающая страховка — та же дыра, только меньше. Состояние, а не событие: снимает
   * первый успешный проход того же подметальщика. Дедуп — имя подметальщика в `reason`.
   */
  maintenance_sweeper_failed: {
    severity: 'error',
    kind: 'condition',
    dedupFields: ['reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  /**
   * Конфигурация не покрывает то, что код обязан делать (P2, ревизия 2026-10-18): у модели,
   * которой платит сервис, нет цены в `llm-prices.yaml` (`reason` = `price:<модель>`), у
   * продюсера heartbeat нет ожидания watchdog (`watchdog:<ключ>`). Critical: модель без цены
   * четыре недели выключала бюджет NFR-1 для всего расхода писателя. Состояние: снимает
   * следующая проверка, увидевшая покрытие (прайс-лист читается без рестарта).
   */
  config_coverage_gap: {
    severity: 'critical',
    kind: 'condition',
    dedupFields: ['reason'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  /**
   * Ответов ≥ 400 одного маршрута и кода за сутки (UTC) больше порога (P2, находка 50):
   * петля 409/429 клиента или серия 5xx бэкенда, которые раньше не оставляли следа ни в
   * журнале, ни в базе. Инцидент дня: поднимается на пороге один раз, счётчик и журнал —
   * вкладка «Ошибки за сутки». Дедуп — маршрут с кодом (`reason`) и сутки.
   */
  http_error_burst: {
    severity: 'warning',
    kind: 'incident',
    dedupFields: ['reason', 'period'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
    periodGrain: 'day',
  },
  /**
   * Бэкенд навсегда отверг отчёт о расходе модели — `422 unknown_model_price` и подобные
   * (P2, находка 6 ревизии 2026-10-18). Четыре недели писатель пересылал такие тела молча, и
   * бюджет NFR-1 не видел ни доллара его расхода. Тела уходят в файл мёртвых писем сервиса;
   * алерт — один на бренд, код отказа и сутки. Инцидент: отказ уже случился, и снимать нечего.
   */
  usage_report_rejected: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['brand', 'reason', 'period'],
    to: ['operator'],
    raisedBy: ['writer', 'monitor'],
    phase: 2,
    periodGrain: 'day',
  },
  approval_deadline: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['draft_id', 'version'],
    to: ['approver', 'operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  draft_new_version: {
    severity: 'info',
    kind: 'incident',
    dedupFields: ['draft_id', 'version'],
    to: ['approver', 'operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  freshness_expiring: {
    severity: 'warning',
    kind: 'condition',
    dedupFields: ['draft_id', 'version'],
    to: ['approver', 'operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
  /**
   * Отказ КАРТОЧКИ: рендер не уложился в таймаут, генератор упал, формат требует медиа
   * (03-generation.md §9.6). Момент подъёма — ДО создания черновика: карточка рисуется
   * перед `POST /intake/v1/media` и перед версией, поэтому ни `draft_id`, ни `version` в
   * этот момент не существуют, и дедуп по ним делал бы алерт неподъёмным по построению.
   * Область та же, что у `draft_generation_failed`: бренд и тема, породившая работу.
   */
  visual_generation_failed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['brand', 'topic_id'],
    to: ['operator'],
    raisedBy: ['writer'],
    phase: 2,
  },
  /**
   * Отказ ТЕКСТОВОЙ части: модель не дала валидной разметки за N попыток,
   * stop_reason 'refusal', серия упала на 429 (03-generation.md §3.9). draft_id ещё нет —
   * черновик не родился, поэтому дедуп по теме.
   */
  draft_generation_failed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['brand', 'topic_id'],
    to: ['operator'],
    raisedBy: ['writer'],
    phase: 2,
  },
  /**
   * Единица работы закрыта ОТКАЗОМ (P3 ревью 2026-10-18, `domain/work-items.ts`): тема снята
   * писателем после исчерпанных или неизлечимых попыток, заявка на генерацию или переделка
   * закрыта отказом. Один алерт на работу, а не на попытку: дедуп — бренд и запись работы
   * (`invocation_id` = `<вид>:<id>`). Отмена человеком и срок темы алерта не дают.
   */
  work_item_failed: {
    severity: 'error',
    kind: 'incident',
    dedupFields: ['brand', 'invocation_id'],
    to: ['operator'],
    raisedBy: ['backend'],
    phase: 2,
  },
} as const satisfies Record<string, AlertDef>;

export type AlertType = keyof typeof ALERT_REGISTRY;

export const ALERT_TYPE_NAMES = Object.keys(ALERT_REGISTRY) as readonly AlertType[];

export function isAlertType(s: string): s is AlertType {
  return Object.hasOwn(ALERT_REGISTRY, s);
}

export function severityOfAlert(type: AlertType): AlertSeverity {
  return ALERT_REGISTRY[type].severity;
}

export interface Alert {
  readonly type: AlertType;
  readonly severity: AlertSeverity;
  readonly scope: AlertScope;
  readonly title: string;
  readonly detail?: string;
  readonly occurred_at_ms: number;
  readonly dedup_key: string;
}

/**
 * Ключ дедупликации строится ТОЛЬКО из dedupFields реестра, в объявленном порядке.
 * Отсутствующее поле кодируется пустой строкой, чтобы ключ оставался позиционным.
 */
export function dedupKeyOf(type: AlertType, scope: AlertScope): string {
  const fields = ALERT_REGISTRY[type].dedupFields as readonly AlertScopeField[];
  const parts = fields.map((f) => {
    const v = scope[f];
    return v === undefined ? '' : String(v);
  });
  return [type, ...parts].join('|');
}

/**
 * Канал доставки — сменная реализация (карта контрактов DEV-PLAN).
 * Реализация №1 — SpoolAlertChannel: запись в спул на диск, синхронная, переживает
 * process.exit. Это и делает алерт «публикация сломалась» независимым от процесса,
 * который сломался.
 */
export interface AlertChannel {
  raise(type: AlertType, scope: AlertScope, title: string, detail?: string): void;
}
